import type { TranslationKey } from "@/i18n";

/** Where an assignment's slides may come from, as the server folds its students' tickets. */
export interface AssignableReach {
  productIds: string[];
  productLevelCaps: { productId: string; maxLevel: number }[];
  capsApply: boolean;
  verified: boolean;
}

interface GatedProject {
  productIds: string[];
  level: number;
}

/**
 * Why a project cannot be assigned to the selected students, or null when it can. The best-placed
 * of the products they hold decides, which is stricter than asking each student in turn: a lookup
 * by id lets a project ride on a product the ticket does not grant, and the reach cannot say that
 * for a group. A project is held back rather than wrongly offered.
 *
 * An unverified reach means MyBestUser went unread, and nothing may be called out of reach.
 */
export function projectBlockedReason(
  project: GatedProject,
  reach: AssignableReach
): TranslationKey | null {
  if (!reach.verified) return null;
  if (project.productIds.length === 0) return "library.reach.noProduct";

  if (!project.productIds.some((id) => reach.productIds.includes(id))) {
    return "library.reach.studentNoProduct";
  }
  if (!reach.capsApply) return null;

  const caps = new Map(reach.productLevelCaps.map((cap) => [cap.productId, cap.maxLevel]));
  const reachable = project.productIds
    .map((id) => caps.get(id))
    .filter((cap): cap is number => cap != null);

  if (reachable.length === 0) return "library.reach.studentLevelTooLow";

  return project.level <= Math.max(...reachable) ? null : "library.reach.studentLevelTooLow";
}
